import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getStream } from "@/lib/api";
import { useAppStore } from "@/store/appStore";
import { STREAM_COLORS, statusMeta, fmtDate, CRIT_COLORS } from "@/lib/format";
import { GaugeRadial } from "@/components/tmo/GaugeRadial";
import { SectionCard, StatusChip, Sparkline, DriverBar, StreamBadges } from "@/components/tmo/primitives";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceLine, Tooltip,
} from "recharts";
import {
  Globe, Smartphone, Phone, Mail, Radio, Share2, HelpCircle, ArrowRight, CheckCircle2, ShieldAlert,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const CHANNEL_ICON = { Web: Globe, "Mobile App": Smartphone, "IVR / Call Center": Phone, "Email / SMS": Mail, "External Comms": Radio, "Social Media": Share2 };

const CAP_LABELS = {
  no_test_date: "No test date on record — score capped",
  no_evidence: "No test evidence available — score capped",
  fail_under_scenario: "Failed the latest scenario test — score capped",
  descoped: "Application is out of scope",
};

function StatusReasonCell({ r }) {
  if (r.status === "NA") return <StatusChip status={r.status} />;
  const drivers = [...(r.reasons || [])].sort((a, b) => a.value - b.value);
  const caps = (r.caps_applied || []).filter((c) => CAP_LABELS[c]);
  return (
    <UITooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help" data-testid={`status-chip-${r.application_id}`}>
          <StatusChip status={r.status} />
        </span>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-[280px] p-0 overflow-hidden bg-white text-cnp-text border border-cnp-border shadow-lg" data-testid={`status-tooltip-${r.application_id}`}>
        <div className="px-3 py-2 border-b border-cnp-border/40 bg-cnp-bg">
          <div className="text-xs font-semibold text-cnp-text">Why {statusMeta(r.status).label.toLowerCase()}?</div>
          <div className="text-[10px] text-cnp-textMuted">TRI drivers for {r.name}</div>
        </div>
        <div className="px-3 py-2 space-y-1.5">
          {caps.length > 0 && (
            <div className="space-y-1 pb-1.5 mb-1 border-b border-cnp-border/40">
              {caps.map((c) => (
                <div key={c} className="flex items-start gap-1.5 text-[11px] text-cnp-red font-medium">
                  <ShieldAlert size={12} className="shrink-0 mt-0.5" />{CAP_LABELS[c]}
                </div>
              ))}
            </div>
          )}
          {drivers.map((d) => (
            <div key={d.label} className="flex items-center justify-between gap-3 text-[11px]">
              <span className="text-cnp-textMuted truncate">{d.label} <span className="text-cnp-textMuted/60">· {Math.round(d.weight)}%</span></span>
              <span className="tnum font-semibold" style={{ color: d.value >= 90 ? "#00733A" : d.value >= 70 ? "#8A5A00" : "#9A0114" }}>{Math.round(d.value)}</span>
            </div>
          ))}
        </div>
      </TooltipContent>
    </UITooltip>
  );
}

function StatusBreakdown({ b }) {
  const rows = [["Ready", b.ready, "#00A651"], ["At Risk", b.at_risk, "#F5A623"], ["Not Ready", b.not_ready, "#D0021B"], ["N/A", b.na, "#9BA3AF"]];
  return (
    <div className="space-y-1.5">
      {rows.map(([l, v, c]) => (
        <div key={l} className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c }} />{l}</span>
          <span className="tnum font-semibold">{v}</span>
        </div>
      ))}
    </div>
  );
}

export default function StreamPage({ stream }) {
  const scenario = useAppStore((s) => s.scenario);
  const openExplain = useAppStore((s) => s.openExplain);
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["stream", stream, scenario], queryFn: () => getStream(stream, scenario) });
  const [selected, setSelected] = useState(null);
  useEffect(() => { if (data?.rows?.length) setSelected(data.rows.find((r) => r.status !== "READY") || data.rows[0]); }, [data]);

  if (isLoading || !data) return <div className="grid grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-52 rounded-lg" />)}</div>;

  const color = STREAM_COLORS[data.color] || STREAM_COLORS[stream];
  const slot = data.slot;
  const trendData = data.trend.map((p) => ({ date: p.date.slice(5), value: p.value }));

  return (
    <TooltipProvider delayDuration={100}>
    <div className="space-y-4 pb-16" data-testid={`stream-page-${stream}`}>
      {/* Row 1 */}
      <div className="grid grid-cols-12 gap-4">
        <SectionCard title={`${data.label} At A Glance`} className="col-span-12 lg:col-span-4"
          action={<button onClick={() => openExplain({ type: "stream", id: stream, title: data.label })} className="text-cnp-blue"><HelpCircle size={16} /></button>}>
          <div className="flex items-center gap-4">
            <GaugeRadial value={data.score} status={data.status} size={170} label="READY" />
            <div className="flex-1"><StatusBreakdown b={data.breakdown} /></div>
          </div>
          <p className="text-xs text-cnp-textMuted mt-3">{data.breakdown.ready} of {data.breakdown.total} applications ready</p>
        </SectionCard>

        {/* slot primary */}
        <SectionCard title={slot.type === "channels" ? "Key Customer Impact Metrics" : slot.type === "workflows" ? "Testing Health" : slot.type === "shared" ? "Infrastructure Health" : "Executive Information Health"} className="col-span-12 lg:col-span-4">
          {(slot.type === "channels" || slot.type === "shared") && (
            <div className="grid grid-cols-2 gap-3">
              {slot.impact_metrics.map((m, i) => (
                <div key={i} className="text-center rounded-lg border border-cnp-border p-3">
                  <div className="text-xl font-bold tnum" style={{ color }}>{m.value}</div>
                  <div className="text-[10px] text-cnp-textMuted mt-1 leading-tight">{m.label}</div>
                </div>
              ))}
            </div>
          )}
          {slot.type === "workflows" && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-cnp-textMuted">Tests (180 days)</span><span className="tnum font-semibold">{slot.testing_health.tests_180d}</span></div>
              <div className="flex justify-between"><span className="text-cnp-textMuted">Test Success Rate</span><span className="tnum font-semibold">{slot.testing_health.success_rate}%</span></div>
              <div className="flex justify-between"><span className="text-cnp-textMuted">Avg. Days Since Last Test</span><span className="tnum font-semibold">{slot.testing_health.avg_days} <span className="text-[10px] text-cnp-textMuted">(Goal &lt; 60)</span></span></div>
              <div className="flex justify-between items-center"><span className="text-cnp-textMuted">Defects Open</span>
                <span className="flex gap-1.5">
                  <span className="px-1.5 rounded bg-cnp-red/10 text-cnp-red text-xs font-semibold">{slot.testing_health.defects.critical}C</span>
                  <span className="px-1.5 rounded bg-cnp-amber/10 text-cnp-amber text-xs font-semibold">{slot.testing_health.defects.high}H</span>
                  <span className="px-1.5 rounded bg-cnp-bg text-cnp-textMuted text-xs font-semibold">{slot.testing_health.defects.medium}M</span>
                </span></div>
            </div>
          )}
          {slot.type === "outcomes" && (
            <div className="space-y-2">{slot.info_health.map((m, i) => <DriverBar key={i} label={m.label} value={m.value} weight={m.weight} />)}</div>
          )}
        </SectionCard>

        <SectionCard title="Readiness Trend" className="col-span-12 lg:col-span-4">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trendData} margin={{ left: -20, right: 10, top: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E3E8EF" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6B7280" }} interval={Math.ceil(trendData.length / 7)} />
              <YAxis domain={[0, 100]} ticks={[0, 50, 100]} tick={{ fontSize: 10, fill: "#6B7280" }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <ReferenceLine y={data.target} stroke="#9BA3AF" strokeDasharray="5 4" label={{ value: "Target 90%", fontSize: 10, fill: "#6B7280", position: "insideTopRight" }} />
              <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-12 gap-4">
        <SectionCard title={`${data.label} Applications`} className="col-span-12 lg:col-span-8">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-cnp-textMuted text-left border-b border-cnp-border">
                {["Application", "Streams", ...(stream === "SUBSCRIBER" ? ["Primary Channel"] : stream === "TECHNICIAN" ? ["Criticality"] : []), "Readiness", "Last Test", "Confidence", "Status"].map((h) => <th key={h} className="pb-2 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.application_id} onClick={() => setSelected(r)}
                    className={`border-b border-cnp-border/60 cursor-pointer hover:bg-cnp-bg ${selected?.application_id === r.application_id ? "bg-cnp-bg" : ""}`}>
                    <td className="py-2 font-medium text-cnp-text max-w-[200px]"><span className="truncate block max-w-[190px]">{r.name}</span></td>
                    <td className="py-2"><StreamBadges streams={r.streams || []} testid={`stream-badges-${r.application_id}`} /></td>
                    {stream === "SUBSCRIBER" && <td className="py-2 text-cnp-textMuted">{r.primary_channel}</td>}
                    {stream === "TECHNICIAN" && <td className="py-2"><span style={{ color: CRIT_COLORS[r.criticality] }} className="font-semibold">{r.criticality}</span></td>}
                    <td className="py-2"><div className="flex items-center gap-2"><span className="tnum font-semibold w-8">{r.score}%</span><div className="h-1.5 w-14 rounded-full bg-cnp-bg overflow-hidden"><div className="h-full rounded-full" style={{ width: `${r.score}%`, backgroundColor: statusMeta(r.status).color }} /></div></div></td>
                    <td className="py-2 tnum">{fmtDate(r.last_test)}</td>
                    <td className="py-2">{r.confidence}</td>
                    <td className="py-2"><StatusReasonCell r={r} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>

        <div className="col-span-12 lg:col-span-4 space-y-4">
          {slot.type === "channels" && (
            <SectionCard title="Readiness By Channel">
              <div className="space-y-3">
                {slot.channels.map((c) => {
                  const Icon = CHANNEL_ICON[c.channel] || Globe;
                  return (
                    <div key={c.channel} className="flex items-center gap-3">
                      <Icon size={16} className="text-cnp-textMuted shrink-0" />
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-0.5"><span className="font-medium">{c.channel}</span><span className="tnum font-semibold">{c.covered ? `${c.score}%` : "No data"}</span></div>
                        {c.covered ? <div className="h-2 rounded-full bg-cnp-bg overflow-hidden"><div className="h-full rounded-full" style={{ width: `${c.score}%`, backgroundColor: c.score >= 90 ? "#00A651" : c.score >= 70 ? "#F5A623" : "#D0021B" }} /></div>
                          : <div className="text-[10px] text-cnp-red font-medium">Channel not covered</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}
          {slot.distribution && (
            <SectionCard title={slot.dist_label}>
              <div className="space-y-3">
                {slot.distribution.map((c) => (
                  <UITooltip key={c.name}>
                    <TooltipTrigger asChild>
                      <div className="cursor-help" data-testid={`dist-row-${c.name.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
                        <div className="flex justify-between text-xs mb-0.5">
                          <span className="font-medium text-cnp-text">{c.name}{c.covered ? <span className="text-cnp-textMuted"> · {c.count}</span> : null}</span>
                          <span className="tnum font-semibold">{c.covered ? `${c.score}%` : "No apps"}</span>
                        </div>
                        {c.covered ? (
                          <div className="h-2 rounded-full bg-cnp-bg overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(2, c.score)}%`, backgroundColor: c.score >= 90 ? "#00A651" : c.score >= 70 ? "#F5A623" : "#D0021B" }} />
                          </div>
                        ) : (
                          <div className="text-[10px] text-cnp-red font-medium">No apps mapped to this capability</div>
                        )}
                      </div>
                    </TooltipTrigger>
                    {c.covered && (
                      <TooltipContent side="left" className="max-w-[260px] p-0 overflow-hidden bg-white text-cnp-text border border-cnp-border shadow-lg">
                        <div className="px-3 py-2 border-b border-cnp-border/40 bg-cnp-bg">
                          <div className="text-xs font-semibold">{c.name}</div>
                          <div className="text-[10px] text-cnp-textMuted">{c.count} application{c.count === 1 ? "" : "s"} · avg {c.score}%</div>
                        </div>
                        <div className="px-3 py-2 space-y-1 max-h-52 overflow-y-auto">
                          {c.members.map((m) => (
                            <div key={m.name} className="flex items-center justify-between gap-3 text-[11px]">
                              <span className="text-cnp-text truncate">{m.name}</span>
                              <span className="tnum font-semibold" style={{ color: statusMeta(m.status).color }}>{m.score}%</span>
                            </div>
                          ))}
                        </div>
                      </TooltipContent>
                    )}
                  </UITooltip>
                ))}
              </div>
            </SectionCard>
          )}
          {slot.type === "shared" && (
            <SectionCard title="Streams We Support">
              <p className="text-xs text-cnp-textMuted mb-3">Applications in each stream that depend on this shared backbone.</p>
              <div className="grid grid-cols-3 gap-2">
                {slot.supported.map((s) => (
                  <div key={s.name} className="rounded-lg border border-cnp-border p-3 text-center">
                    <div className="text-2xl font-bold tnum text-cnp-navy">{s.score}</div>
                    <div className="text-[10px] text-cnp-textMuted leading-tight mt-1">{s.name.split(" / ")[0]}</div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
          <SectionCard title="Test Readiness Index">
            {data.drivers.map((d, i) => <DriverBar key={i} label={d.label} value={d.value} weight={d.weight} definition={d.definition} />)}
          </SectionCard>
        </div>
      </div>

      {/* Row 3: detail + impact */}
      <div className="grid grid-cols-12 gap-4">
        <SectionCard title="Application Readiness Details" className="col-span-12 lg:col-span-8">
          {selected && (
            <div>
              <div className="flex items-center gap-3 mb-3">
                <h4 className="text-lg font-semibold text-cnp-text">{selected.name}</h4>
                <StatusChip status={selected.status} />
                <button onClick={() => openExplain({ type: "application", id: selected.application_id, name: selected.name, title: selected.name })}
                  data-testid="how-calculated-app-button"
                  className="ml-auto text-xs text-cnp-blue font-semibold flex items-center gap-1 hover:underline"><HelpCircle size={13} /> How is this calculated?</button>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-4">
                {[["Readiness", `${selected.score}%`], ["Last Test", fmtDate(selected.last_test)], ["Confidence", selected.confidence], ["Key Issue", selected.issue || "None"]].map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-cnp-bg p-2.5"><div className="text-[10px] uppercase text-cnp-textMuted">{l}</div><div className="text-sm font-semibold text-cnp-text mt-0.5">{v}</div></div>
                ))}
              </div>
              <div className="text-[11px] uppercase tracking-wide font-semibold text-cnp-blue mb-2">Recommended Actions</div>
              <ol className="space-y-1.5 text-sm">
                {(selected.status === "READY"
                  ? ["Maintain current test cadence", "Continue monitoring key dependencies"]
                  : ["Schedule a fresh scenario test", "Review dependency and capacity headroom", "Confirm failover / redundancy path"]).map((a, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="h-5 w-5 rounded-full bg-cnp-blue/10 text-cnp-blue text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>{a}</li>
                ))}
              </ol>
            </div>
          )}
        </SectionCard>

        <SectionCard title={`${data.label} Impact`} className="col-span-12 lg:col-span-4">
          <p className="text-sm font-medium text-cnp-text mb-2">If these systems are unavailable…</p>
          <ul className="space-y-2 text-sm text-cnp-text">
            {(stream === "SUBSCRIBER"
              ? ["Customers can't view outage information", "Customers can't report outages or hazards", "Calls may fail or face long wait times", "Notifications may be delayed or lost", "Customer trust and safety are at risk"]
              : stream === "TECHNICIAN"
              ? ["Crews lose dispatch and assignment", "Outage picture becomes stale", "Damage assessment slows", "Crew coordination degrades", "Restoration time increases"]
              : stream === "SHARED"
              ? ["Every stream loses its shared backbone at once", "Network, integration and identity services fail", "Customer, field and executive systems cascade down", "A single failure here has the widest blast radius", "Restoration depends on this layer coming back first"]
              : ["Leadership loses situational awareness", "Resource decisions lack current data", "Financial impact is unclear", "Executive comms are delayed", "Compliance reporting is at risk"]).map((t, i) => (
              <li key={i} className="flex items-start gap-2"><ShieldAlert size={15} className="text-cnp-amber mt-0.5 shrink-0" />{t}</li>
            ))}
          </ul>
        </SectionCard>
      </div>

      {/* Sticky verdict footer */}
      <div className="fixed bottom-0 left-[210px] right-0 z-10 border-t px-6 py-3 flex items-center gap-3" data-testid="stream-verdict-footer" style={{ backgroundColor: statusMeta(data.status).bg }}>
        <CheckCircle2 size={20} style={{ color: statusMeta(data.status).color }} />
        <span className="font-bold tnum" style={{ color: statusMeta(data.status).text }}>{data.label.toUpperCase()} READINESS: {data.score}%</span>
        <span className="text-sm" style={{ color: statusMeta(data.status).text }}>{data.verdict}</span>
      </div>
    </div>
    </TooltipProvider>
  );
}
